<template>
  <div class="box__content">
    <div class="box__header">
      <img class="subtitle__icon" src="@/assets/icons/icn_money-accent.svg" alt="">
      <h2 class="box__title">{{ $t(`${namespace}.title`) }}</h2>
      <v-spacer />
      <router-link
        v-if="hasRole('profiles', 'ROLE_ADMINISTRATOR') || hasRole('profiles', 'ROLE_FINANCIAL_ADMIN') || hasRole('billing', 'ROLE_PUT_FINANCIAL_ACCOUNT_EMAILS_TO_NOTIFY')"
        class="box__header__edit"
        :to="{
          name: 'CompanyFinancialConfig',
          params: { id: companyId }
        }"
      >
        {{ $t('Common.edit') }} <v-icon color="accent">edit</v-icon>
      </router-link>
    </div>

    <div v-if="isSuccess">
      <div class="box__body min-height">
        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div>
              <div class="caption gray-6--text">{{ $t(`${namespace}.billingStart`) }}</div>
              <div v-if="serviceStartChargeDate">{{ serviceStartChargeDate | formatDate('DD/MM/YYYY') }}</div>
              <div v-else>{{ $t('Common.uninformed') }}</div>
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.revenues`) }}</div>
              <div v-if="periodicityConfigType">{{ periodicity }}</div>
              <div v-else>{{ $t('Common.uninformed') }}</div>
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.invoiceExpiration`) }}</div>
              <div v-if="dueDays">{{ dueDays | formatNumber }}</div>
              <div v-else>{{ $t('Common.uninformed') }}</div>
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.paymentMethod`) }}</div>
              <div v-if="paymentMethod">
                <v-chip class="mx-0 mb-0">{{ paymentMethodName }}</v-chip>
              </div>
              <div v-else>{{ $t('Common.uninformed') }}</div>
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.monthlyServiceFee`) }}</div>
              <div v-if="serviceMonthlyFee">{{ serviceMonthlyFee | currencyBRL }}</div>
              <div v-else>{{ $t(`${namespace}.free`) }}</div>
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.feePerRunMade`) }}</div>
              <div v-if="serviceTaxiJobFee">{{ serviceTaxiJobFee | currencyBRL }}</div>
              <div v-else>{{ $t(`${namespace}.free`) }}</div>
            </div>
          </v-flex>
        </v-layout>

        <v-layout v-if="false" row wrap>
          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.invoiceDiscount`) }}</div>
              <div><code>[TO DO]</code></div>
              <!-- <div v-else>{{ $t('Common.uninformed') }}</div> -->
            </div>
          </v-flex>

          <v-flex sm6 xs12>
            <div class="mt-3">
              <div class="caption gray-6--text">{{ $t(`${namespace}.ridesNumber`) }}</div>
              <div><code>[TO DO]</code></div>
              <!-- <div v-else>{{ $t('Common.uninformed') }}</div> -->
            </div>
          </v-flex>
        </v-layout>

        <div class="mt-3">
          <div class="caption gray-6--text">{{ $t(`${namespace}.financialOfficer`) }}</div>
          <div v-if="isValidArray(emailsToNotify)">
            <span
              v-for="(email, index) in emailsToNotify"
              :key="email"
            >
              <span v-if="emailsToNotify.length > 1 && index === emailsToNotify.length - 1"> {{ $t('Common.and') }} </span>
              <a
                :href="`mailto:${email}`"
                title=""
                class="text--link accent--text"
              >{{ email }}</a>
              <span v-if="index < emailsToNotify.length - 2">, </span>
            </span>
          </div>
          <div v-else>{{ $t('Common.uninformed') }}</div>
        </div>
      </div>

      <div v-if="false" class="box__body box__body--border-top">
        <div class="mb-3">
          <div class="caption gray-6--text">{{ $t(`${namespace}.defaultMessageForUsers`) }}</div>
          <div><code>[TO DO]</code></div>
          <!-- <div v-else>{{ $t('Common.uninformed') }}</div> -->
        </div>

        <div>
          <div class="caption gray-6--text">{{ $t(`${namespace}.defaultMessageForEmployees`) }}</div>
          <div><code>[TO DO]</code></div>
          <!-- <div v-else>{{ $t('Common.uninformed') }}</div> -->
        </div>
      </div>
    </div>

    <v-layout
      v-if="isFailed"
      class="text-xs-center min-height"
      align-center
      justify-center
      row
      fill-height
    >
      <v-flex class="text-xs-center">
        <span>
          {{ $t('Common.genericErrorMsg') }}<br>
          <a
            href="#"
            :title="$t('Common.tryAgain')"
            class="text--link accent--text"
            @click.prevent="getWalletFinancialConfig"
          >
            {{ $t('Common.tryAgain') }}
          </a>
        </span>
      </v-flex>
    </v-layout>

    <v-layout
      v-if="isLoading"
      class="text-xs-center min-height"
      align-center
      justify-center
      row
      fill-height
    >
      <v-progress-circular indeterminate color="accent" />
    </v-layout>
  </div>
</template>

<script>
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, hasRole } from 'utils/functions'

export default {
  name: 'CompanyFinancialConfig',
  computed: {
    companyId () {
      return this.$route.params.id
    },

    periodicity () {
      const options = {
        'MONTHLY': this.$t(`${this.namespace}.monthlyPeriod`),
        'MANY_DAYS': this.$t(`${this.namespace}.fortnightlyPeriod`),
        'DAILY': this.$t(`${this.namespace}.dailyPeriod`)
      }

      return options[this.periodicityConfigType] || '-'
    },

    paymentMethodName () {
      const options = {
        'BOLETO': this.$t(`${this.namespace}.bankBillet`),
        'CREDIT_CARD': this.$t(`${this.namespace}.creditCard`),
        'BANK_TRANSFER': this.$t(`${this.namespace}.bankTransfer`)
      }

      return options[this.paymentMethod]
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Components.CompanyFinancialConfig',
      currentStatus: null,

      // fields
      financialAccountId: null,
      periodicityConfigType: null,
      paymentMethod: null,
      dueDays: null,
      emailsToNotify: [],
      isSaasActive: false,
      serviceStartChargeDate: null,
      serviceMonthlyFee: null,
      serviceTaxiJobFee: null
    }
  },
  methods: {
    isValidArray (val) {
      return isArray(val)
    },

    hasRole (group, role) {
      return hasRole(group, role)
    },

    async getWalletFinancialConfig () {
      if (!this.companyId) return

      this.currentStatus = STATUS_IS_LOADING

      try {
        const { data: res } = await this.$apiService.getWalletFinancialConfig(this.companyId)

        if (!res || !res.valid) {
          throw new Error(res.reasons[0])
        }

        if (res.content) {
          const {
            financialAccountId,
            periodicityConfigType,
            paymentMethod,
            dueDays,
            emailsToNotify,
            isSaasActive,
            serviceStartChargeDate,
            serviceMonthlyFee,
            serviceTaxiJobFee
          } = res.content

          this.financialAccountId = financialAccountId
          this.periodicityConfigType = periodicityConfigType
          this.paymentMethod = paymentMethod
          this.dueDays = dueDays
          this.emailsToNotify = emailsToNotify
            .map(email => email.trim())
            .filter(email => email)
          this.isSaasActive = isSaasActive
          this.serviceStartChargeDate = serviceStartChargeDate
          this.serviceMonthlyFee = serviceMonthlyFee
          this.serviceTaxiJobFee = serviceTaxiJobFee
        }

        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR', err)
        this.currentStatus = STATUS_FAILED
      }
    }
  },
  mounted () {
    this.getWalletFinancialConfig()
  }
}
</script>

<style lang="scss" scoped>
.box__content {
  border: 1px solid $gray-2;
  background-color: #fff;
  max-width: 840px;
  transition: box-shadow .26s;
  border-radius: 4px;

  &:hover {

    @media (min-width: 992px) {
      box-shadow: 0 1px 6px rgba(#000, .25);
    }
  }
}

.box__header {
  padding: 16px 16px 0;
  color: $gray-7;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  position: relative;
}

.box__header__edit {
  align-items: center;
  color: $accent;
  display: flex;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: normal;
  text-decoration: none;

  .v-icon {
    font-size: 16px;
    margin-left: 4px;
  }
}

.subtitle__icon,
.box__title {
  display: inline-block;
  vertical-align: middle;
}

.subtitle__icon {
  margin-right: 10px;
  width: 24px;
}

.box__title {
  font-size: 18px;
  font-weight: 800;
}

.box__subtitle,
.box__desc {
  margin-bottom: 0
}

.box__subtitle {
  font-size: 14px;
}

.box__desc {
  color: $gray-6;
  font-size: 12px;
}

.box__body {
  padding: 16px;
}

.box__body--border-top {
  border-top: 1px solid #f0f0f0;
}

.min-height {
  min-height: 300px;
}
</style>
